import { Action } from "@ngrx/store";

import {
  Config,
  JSFunctions,
} from "src/app/features/designer-studio/settings/settings.model";

import {
  AppField,
  FormProperties,
  Trigger,
  ImportedService,
  View,
  ServiceContexts,
  OnHandlerService,
  PrintSetting,
  MirrorGroups,
  Doc,
  Filter,
  PopupFieldData,
  StageDefaultInfo,
  PopupTableDetails,
} from "../../models/doc.model";

import { SelectedService } from "../../../shared/models/Service.model";

export enum DocActionTypes {
  // LOCAL
  CLEAN_LOCAL = "[Local] Clean localstorage data",

  // STATE
  SET_STATE = "[State] Set State",
  SET_HAS_CHANGES = "[State] Set Has Changes",

  // CONFIG
  SET_CONFIG = "[Config] Set Config",

  // All PERMISSIONS
  SET_SYM_PERMISSIONS_LIST = "[SymPermissions] Set sym permissions list",
  LOAD_SYM_PERMISSIONS_LIST = "[SymPermissions] Load sym permissions list",

  // DOC
  SET_DOC = "[Doc] Set Doc",
  LOAD_DOC = "[Doc] Load Doc",
  SET_SELECTED_FIELD = "[Doc] Set Selected Field",
  SET_SELECTED_RADIO = "[Doc] Set Selected Radio Button",
  SET_DOC_LOADED = "[Doc] Set Doc Loaded",
  UPDATE_APP_FIELD = "[Doc] Update App Field",
  UPDATE_APP_FIELDS = "[Popup] Update App Fields",
  UPDATE_APP_FIELD_DISPLAY_NAME = "[Popup] Update popup display name",
  UPDATE_APP_FIELD_TAB_ORDER = "[Doc] Update App Field Tab Order",
  UPDATE_FORM_PROPS = "[Doc] Update Form Properties",
  SET_DEFAULT_FORM_PROPS = "[Doc] Set Default Form Properties",
  UPDATE_FORM_PRINTER = "[Doc] Update Form Printer",
  SET_DOC_LOADED_FAILURE = "[Doc] Set Doc Loaded Failure",
  RESET_STATE = "[Doc] Reset State",
  UPDATE_APP_FIELD_VALUE = "[Doc] Update App Field Value",
  SET_PAGE_COUNT = "[Doc] Set Page Count",
  SET_IMPORTED_SERVICES = "[Doc] Set Imported Services in Doc",
  ADD_FIELD_TRIGGER = "[Doc] Add Field Trigger",
  UPDATE_FIELD_TRIGGERS = "[Doc] Update Field Triggers",
  SET_CURRENT_RQ_MAPPING = "[Doc] Set current request mapping",
  SET_CURRENT_CONTEXT = "[Doc] Set current context mapping",
  UPDATE_SERVICE_CONTEXT = "[Doc] Update current service context",
  SET_SERVICE = "[Doc] set current selected service",
  UPDATE_FUNCTIONS = "[Doc] update functions in doc",
  SET_FUNCTIONS = "[Doc] set functions in doc when opening",
  UPDATE_INBOUNDING_VARIABLES = "[Doc] update inbounding variables in doc",
  UPDATE_OUTBOUNDING_VARIABLES = "[Doc] update outbounding variables in popup",
  ADD_VIEW = "[Doc] Add view",
  UPDATE_FORM_CATEGORIES = "[Doc] Update form categories",
  UPDATE_FORM_ACTIONS = "[Doc] Update Form onLoad/onSubmit actions",
  UPDATE_FORM_TRIGGER = "[Doc] Update Form onLoad/onSubmit Trigger",
  UPDATE_VARIABLES = "[Doc] update variables in doc",
  SET_VARIABLES = "[Doc] set variables in doc when opening",
  SET_CURRENT_RS_MAPPING = "[Doc] Set current response mapping",
  UPDATE_TRIGGER_PRE_HOOK = "[Doc] Update service trigger PRE",
  UPDATE_TRIGGER_POST_HOOK = "[Doc] Update service trigger POST",
  SET_CURRENT_RS_CTX = "[Doc] Set current response variable mapping",
  SET_CURRENT_RQ_CTX = "[Doc] Set current request variable mapping",
  SET_CURRENT_RS_ENV = "[Doc] Set current response environment mapping",
  SET_CURRENT_RQ_ENV = "[Doc] Set current request environment mapping",
  SET_HTML_ELEMENTS_LIST = "[Doc] Set HTML elements",
  UPDATE_HTML_ELEMENT = "[Doc] Update HTML elements",
  UPDATE_FIELD_FILTER = "[Doc] Update field filter",
  DELETE_SERVICE_CONTEXT = "[Doc] Delete Service Context",
  DELETE_IMPORTED_SERVICE = "[Doc] Delete Imported Service",
  UPDATE_HELP_VALUE = "[Doc] Update app field help value",
  UPDATE_FIELD_TYPE = "[Doc] Update app field type",
  UPDATE_FIELD_TRIGGER_CUSTOM_HANDLER = "[Doc] Update field trigger custom handler",
  UPDATE_FORM_WORK_AREAS = "[Doc] Update form work areas",
  UPDATE_FORM_PERMISSIONS = "[Doc] Update form permissions",
  UPDATE_FORM_PRIVILEGE_GROUPS = "[Doc] Update form privilege groups",
  UPDATE_VIEW_MIRROR_GROUPS = "[Doc] Update views mirror groups",
  UPDATE_ONLOAD = "[Doc] Update On Load",
  UPDATE_VIEWS = "[Doc] Update views",

  // Popups
  ADD_POPUP_FIELD = "[Doc][Popup] Add popup field",
  REMOVE_POPUP_FIELD = "[Doc][Popup] Remove popup field",
  UPDATE_POPUP_FIELD = "[Doc][Popup] Update popup field",
  UPDATE_POPUP_STAGE_DATA = "[Popup] Update popup stage data",
  UPDATE_POPUP_TABLE_DATA = "[Popup] Update popup table data",
  UPDATE_POPUP_TABLE_CONTROLS = "[Popup] Update popup buttons and title",
}

// LOCALSTORAGE ACTIONS
export class CleanLocal implements Action {
  readonly type = DocActionTypes.CLEAN_LOCAL;

  constructor(public docId: string) {}
}

// STATE ACTIONS
export class SetState implements Action {
  readonly type = DocActionTypes.SET_STATE;

  constructor(public state: any) {}
}

// CONFIG ACTIONS
export class SetConfig implements Action {
  readonly type = DocActionTypes.SET_CONFIG;

  constructor(public config: Config) {}
}

// DOC ACTIONS
export class SetDoc implements Action {
  readonly type = DocActionTypes.SET_DOC;

  constructor(public doc: Doc) {}
}

export class ResetState implements Action {
  readonly type = DocActionTypes.RESET_STATE;

  constructor() {}
}

export class SetSelectedField implements Action {
  readonly type = DocActionTypes.SET_SELECTED_FIELD;

  constructor(public fieldId: string) {}
}

export class SetSelectedRadio implements Action {
  readonly type = DocActionTypes.SET_SELECTED_RADIO;

  constructor(public fieldId: string) {}
}

export class LoadDoc implements Action {
  readonly type = DocActionTypes.LOAD_DOC;

  constructor(public docId: string, public docType: string) {}
}

export class SetImportedServices implements Action {
  readonly type = DocActionTypes.SET_IMPORTED_SERVICES;

  constructor(public importedServices: ImportedService[]) {}
}

export class SetService implements Action {
  readonly type = DocActionTypes.SET_SERVICE;

  constructor(public service: SelectedService) {}
}

export class UpdateAppFieldType implements Action {
  readonly type = DocActionTypes.UPDATE_FIELD_TYPE;

  constructor(
    public data: { type: string; format: string; maxLength: number }
  ) {}
}

export class UpdateHelpValueFromAppField implements Action {
  readonly type = DocActionTypes.UPDATE_HELP_VALUE;

  constructor(public data: string, public choice: string = "HELPFILE") {}
}

export class setCurrentContext implements Action {
  readonly type = DocActionTypes.SET_CURRENT_CONTEXT;

  constructor(public context: string) {}
}

export class UpdateServiceContext implements Action {
  readonly type = DocActionTypes.UPDATE_SERVICE_CONTEXT;

  constructor(public contextName: string, public mapping: ServiceContexts) {}
}

export class UpdateFunctions implements Action {
  readonly type = DocActionTypes.UPDATE_FUNCTIONS;

  constructor(public funcs: JSFunctions) {}
}

export class UpdateVariables implements Action {
  readonly type = DocActionTypes.UPDATE_VARIABLES;

  constructor(public vars: any) {}
}

export class SetFunctions implements Action {
  readonly type = DocActionTypes.SET_FUNCTIONS;

  constructor(public funcs: JSFunctions) {}
}

export class SetVariables implements Action {
  readonly type = DocActionTypes.SET_VARIABLES;

  constructor(public vars: any) {}
}

export class UpdateInboundingVariables implements Action {
  readonly type = DocActionTypes.UPDATE_INBOUNDING_VARIABLES;

  constructor(public inbounds: any) {}
}

export class UpdateOutboundingVariables implements Action {
  readonly type = DocActionTypes.UPDATE_OUTBOUNDING_VARIABLES;

  constructor(public outbounds: any) {}
}

export class setCurrentRqMapping implements Action {
  readonly type = DocActionTypes.SET_CURRENT_RQ_MAPPING;

  constructor(public rqViewMapping: { [key: string]: string }) {}
}

export class setCurrentRsMapping implements Action {
  readonly type = DocActionTypes.SET_CURRENT_RS_MAPPING;

  constructor(public rsViewMapping: { [key: string]: string }) {}
}

export class setCurrentRsCtx implements Action {
  readonly type = DocActionTypes.SET_CURRENT_RS_CTX;

  constructor(public rsCtxMapping: { [key: string]: string }) {}
}

export class setCurrentRqCtx implements Action {
  readonly type = DocActionTypes.SET_CURRENT_RQ_CTX;

  constructor(public rqCtxMapping: { [key: string]: string }) {}
}

export class setCurrentRsEnv implements Action {
  readonly type = DocActionTypes.SET_CURRENT_RS_ENV;

  constructor(public rsEnvMapping: { [key: string]: string }) {}
}

export class setCurrentRqEnv implements Action {
  readonly type = DocActionTypes.SET_CURRENT_RQ_ENV;

  constructor(public rqEnvMapping: { [key: string]: string }) {}
}

export class SetHtmlElements implements Action {
  readonly type = DocActionTypes.SET_HTML_ELEMENTS_LIST;

  constructor(public payload: { htmlElements: any[] }) {}
}

export class UpdateHtmlElement implements Action {
  readonly type = DocActionTypes.UPDATE_HTML_ELEMENT;

  constructor(public payload: { key: string; value: any; id: string }) {}
}

export class SetDocLoaded implements Action {
  readonly type = DocActionTypes.SET_DOC_LOADED;
}

export class SetDocLoadedFailure implements Action {
  readonly type = DocActionTypes.SET_DOC_LOADED_FAILURE;
}

export class UpdateAppField implements Action {
  readonly type = DocActionTypes.UPDATE_APP_FIELD;

  constructor(public payload: AppField) {}
}

export class UpdateAppFields implements Action {
  readonly type = DocActionTypes.UPDATE_APP_FIELDS;

  constructor(public payload: Record<string, AppField>) {}
}

export class UpdateFormProperties implements Action {
  readonly type = DocActionTypes.UPDATE_FORM_PROPS;

  constructor(public payload: FormProperties) {}
}

export class SetDefaultFormProperties implements Action {
  readonly type = DocActionTypes.SET_DEFAULT_FORM_PROPS;

  constructor(public payload: FormProperties) {}
}

export class UpdateAppFieldValue implements Action {
  readonly type = DocActionTypes.UPDATE_APP_FIELD_VALUE;

  constructor(public payload: { value: any }) {}
}

export class UpdateAppFieldTabOrder implements Action {
  readonly type = DocActionTypes.UPDATE_APP_FIELD_TAB_ORDER;

  constructor(public payload: { name: string; order: number }) {}
}

export class SetPageCount implements Action {
  readonly type = DocActionTypes.SET_PAGE_COUNT;

  constructor(public payload: number) {}
}

export class AddFieldTrigger implements Action {
  readonly type = DocActionTypes.ADD_FIELD_TRIGGER;

  constructor(public payload: any) {}
}

export class UpdateFieldTriggers implements Action {
  readonly type = DocActionTypes.UPDATE_FIELD_TRIGGERS;

  constructor(public payload: Trigger) {}
}

export class AddView implements Action {
  readonly type = DocActionTypes.ADD_VIEW;

  constructor(public payload: View) {}
}

export class UpdateFormCategories implements Action {
  readonly type = DocActionTypes.UPDATE_FORM_CATEGORIES;

  constructor(public categories: string[]) {}
}

export class updateFieldTriggerCustomHandler implements Action {
  readonly type = DocActionTypes.UPDATE_FIELD_TRIGGER_CUSTOM_HANDLER;

  constructor(public payload: string) {}
}

export class UpdateFormActions implements Action {
  readonly type = DocActionTypes.UPDATE_FORM_ACTIONS;

  constructor(
    public payload: { services: OnHandlerService[]; formView: string }
  ) {}
}

export class UpdateFormTrigger implements Action {
  readonly type = DocActionTypes.UPDATE_FORM_TRIGGER;

  constructor(public payload: { customTrigger: string; formView: string }) {}
}

export class UpdateTriggerPreHook implements Action {
  readonly type = DocActionTypes.UPDATE_TRIGGER_PRE_HOOK;

  constructor(public payload: string) {}
}

export class UpdateFieldFilter implements Action {
  readonly type = DocActionTypes.UPDATE_FIELD_FILTER;

  constructor(public payload: Filter) {}
}

export class DeleteServiceContext implements Action {
  readonly type = DocActionTypes.DELETE_SERVICE_CONTEXT;

  constructor(public payload: string) {}
}

export class DeleteImportedService implements Action {
  readonly type = DocActionTypes.DELETE_IMPORTED_SERVICE;

  constructor(public payload: { serviceGroup: string; service: string }) {}
}

export class UpdateTriggerPostHook implements Action {
  readonly type = DocActionTypes.UPDATE_TRIGGER_POST_HOOK;

  constructor(public payload: string) {}
}

export class UpdateFormPrinter implements Action {
  readonly type = DocActionTypes.UPDATE_FORM_PRINTER;

  constructor(public payload: PrintSetting | null) {}
}

export class UpdateFormWorkAreas implements Action {
  readonly type = DocActionTypes.UPDATE_FORM_WORK_AREAS;

  constructor(public workAreas: string[]) {}
}

export class UpdateFormPermissions implements Action {
  readonly type = DocActionTypes.UPDATE_FORM_PERMISSIONS;

  constructor(public permissions: string[]) {}
}

export class UpdateFormPrivilegeGroups implements Action {
  readonly type = DocActionTypes.UPDATE_FORM_PRIVILEGE_GROUPS;

  constructor(public privilegeGroups: number[]) {}
}

export class UpdateViewMirrorGroups implements Action {
  readonly type = DocActionTypes.UPDATE_VIEW_MIRROR_GROUPS;

  constructor(public mirrorGroups: MirrorGroups) {}
}

export class UpdateOnLoad implements Action {
  readonly type = DocActionTypes.UPDATE_ONLOAD;

  constructor(public onLoad: string[]) {}
}

export class UpdateViews implements Action {
  readonly type = DocActionTypes.UPDATE_VIEWS;

  constructor(public views: { [key: string]: View }) {}
}

// Popup Actions
export class AddPopupField implements Action {
  readonly type = DocActionTypes.ADD_POPUP_FIELD;

  constructor(public appField: AppField, public popupField: PopupFieldData) {}
}

export class RemovePopupField implements Action {
  readonly type = DocActionTypes.REMOVE_POPUP_FIELD;

  constructor(public fieldId: string) {}
}

export class UpdatePopupField implements Action {
  readonly type = DocActionTypes.UPDATE_POPUP_FIELD;
  constructor(public popupFieldData: PopupFieldData) {}
}

export class UpdatePopupStageData implements Action {
  readonly type = DocActionTypes.UPDATE_POPUP_STAGE_DATA;
  constructor(public payload: StageDefaultInfo) {}
}

export class UpdatePopupTableControls implements Action {
  readonly type = DocActionTypes.UPDATE_POPUP_TABLE_CONTROLS;
  constructor(
    public title: string,
    public pbutton: string,
    public sbutton: string
  ) {}
}

export class UpdatePopupTableDetails implements Action {
  readonly type = DocActionTypes.UPDATE_POPUP_TABLE_DATA;
  constructor(public payload: PopupTableDetails) {}
}

export class UpdatePopupFieldDisplayName implements Action {
  readonly type = DocActionTypes.UPDATE_APP_FIELD_DISPLAY_NAME;
  constructor(
    public payload: { viewName: string; fieldId: string; newName: string }
  ) {}
}

export type DocActions =
  | SetState
  | SetDoc
  | SetConfig
  | ResetState
  | LoadDoc
  | SetSelectedField
  | SetDocLoaded
  | UpdateAppField
  | UpdateAppFields
  | SetDocLoadedFailure
  | UpdateFormProperties
  | UpdateAppFieldValue
  | UpdateAppFieldTabOrder
  | UpdateServiceContext
  | SetPageCount
  | AddFieldTrigger
  | UpdateFieldTriggers
  | SetService
  | setCurrentRqMapping
  | UpdateFunctions
  | UpdateInboundingVariables
  | setCurrentContext
  | UpdateFormCategories
  | UpdateFormActions
  | UpdateFormTrigger
  | UpdateVariables
  | setCurrentRsMapping
  | UpdateTriggerPostHook
  | UpdateTriggerPreHook
  | setCurrentRsCtx
  | setCurrentRqCtx
  | UpdateFieldFilter
  | DeleteServiceContext
  | UpdateHelpValueFromAppField
  | UpdateFormWorkAreas
  | UpdateFormPermissions
  | UpdateFormPrivilegeGroups
  | UpdateViewMirrorGroups
  | UpdateOnLoad
  | UpdateViews
  | AddPopupField
  | RemovePopupField
  | UpdatePopupTableDetails
  | UpdatePopupTableControls;
